import React, { type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import QRCode from 'react-qr-code'
import { CheckCircle2, Copy, Download, Mail, ScanLine, ShieldCheck, Smartphone } from 'lucide-react'
import { Button, InlineError, Spinner } from '@asad/crane-ui'

import { MfaCodeInput } from './MfaCodeInput'
import { MfaLinkButton, MfaMethodButton, MfaStepHeader } from './MfaSteps'

/** Kroki konfiguracji MFA — prezentacja przeniesiona z
 *  `app/prototypes/src/components/auth/MfaSetupSteps.tsx`
 *  (method-select → qr → confirm → backup lub method-select → email-otp → backup).
 *
 *  Jedyna świadoma różnica wobec prototypu: krok QR renderuje PRAWDZIWY kod
 *  (`react-qr-code`, 160 px) zamiast prototypowej atrapy `FakeQr` — prototyp rysuje
 *  wzór tylko dlatego, że nie może dodać zależności, a atrapą nie da się
 *  skonfigurować aplikacji uwierzytelniającej. Ramka i wymiary bez zmian. */

type MfaSetupMethod = 'TOTP' | 'EMAIL'

/* ── MfaSetupMethodSelectStep ───────────────────────────────────────────── */

export type MfaSetupMethodSelectStepProps = {
  emailSending: boolean
  emailError: string
  currentMethod?: MfaSetupMethod
  onPickEmail: () => void
  onPickTotp: () => void
  hideBackButton?: boolean
  /** Powrót — w modalu profilu krok ma własne zamknięcie, więc bywa pominięty. */
  onBack?: () => void
}

export const MfaSetupMethodSelectStep = ({
  emailSending,
  emailError,
  currentMethod,
  onPickEmail,
  onPickTotp,
  hideBackButton,
  onBack,
}: MfaSetupMethodSelectStepProps) => {
  const { t } = useTranslation()

  return (
    <div className='space-y-3.5'>
      <MfaStepHeader
        icon={<ShieldCheck className='size-5' aria-hidden />}
        title={t('mfaSetup.title')}
        subtitle={t('mfaSetup.subtitle')}
      />
      <div className='flex flex-col gap-2.5'>
        <MfaMethodButton
          icon={<Smartphone className='size-4' aria-hidden />}
          label={t('mfaVerify.methodTotp')}
          description={currentMethod === 'TOTP' ? t('mfaSetup.currentMethod') : t('mfaSetup.methodTotpDesc')}
          disabled={currentMethod === 'TOTP'}
          onClick={onPickTotp}
        />
        <MfaMethodButton
          icon={<Mail className='size-4' aria-hidden />}
          label={emailSending ? t('mfaVerify.sendingEmail') : t('mfaVerify.methodEmail')}
          description={currentMethod === 'EMAIL' ? t('mfaSetup.currentMethod') : t('mfaSetup.methodEmailDesc')}
          disabled={emailSending || currentMethod === 'EMAIL'}
          onClick={onPickEmail}
        />
      </div>
      {emailError ? <InlineError>{emailError}</InlineError> : null}
      {!hideBackButton && onBack ? (
        <div className='flex justify-center'>
          <MfaLinkButton muted onClick={onBack}>
            {t('mfaVerify.back')}
          </MfaLinkButton>
        </div>
      ) : null}
    </div>
  )
}

/* ── MfaSetupQrStep ─────────────────────────────────────────────────────── */

export type MfaSetupQrStepProps = {
  otpauthUrl: string
  secret: string
  secretCopied: boolean
  onCopySecret: () => void
  onNext: () => void
  onBack: () => void
}

export const MfaSetupQrStep = ({
  otpauthUrl,
  secret,
  secretCopied,
  onCopySecret,
  onNext,
  onBack,
}: MfaSetupQrStepProps) => {
  const { t } = useTranslation()

  return (
    <div className='space-y-3.5'>
      <MfaStepHeader
        icon={<ScanLine className='size-5' aria-hidden />}
        title={t('mfaSetup.qrTitle')}
        subtitle={t('mfaSetup.qrInstruction')}
      />

      <div className='flex justify-center'>
        {/* Kod QR jest z natury czarno-biały w obu motywach — biała płaszczyzna
            pod modułami jest wymagana przez czytniki, nie jest kolorem motywu. */}
        <div
          className='grid size-40 place-items-center rounded-crane-md bg-white shadow-crane-soft'
          role='img'
          aria-label={t('mfaSetup.qrAriaLabel')}
        >
          {otpauthUrl ? (
            <QRCode value={otpauthUrl} size={144} style={{ width: 'min(144px, 100%)', height: 'auto' }} />
          ) : (
            <Spinner className='text-crane-muted-foreground' />
          )}
        </div>
      </div>

      <div className='rounded-crane-md bg-crane-card-inset p-3.5'>
        <div className='mb-1 text-crane-xs font-semibold uppercase tracking-crane-label text-crane-muted-foreground'>
          {t('mfaSetup.secretLabel')}
        </div>
        <div className='flex flex-col items-stretch gap-2.5 md:flex-row md:items-center md:justify-between'>
          <code className='min-w-0 break-all text-crane-lg font-semibold tracking-crane-wide text-crane-foreground'>
            {secret}
          </code>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='min-h-11 shrink-0 whitespace-normal md:min-h-0 md:whitespace-nowrap'
            onClick={onCopySecret}
          >
            {secretCopied ? <CheckCircle2 className='text-crane-status-online' aria-hidden /> : <Copy aria-hidden />}
            {secretCopied ? t('mfaSetup.copied') : t('commonActions.copy')}
          </Button>
        </div>
      </div>

      <Button
        type='button'
        className='min-h-11 w-full whitespace-normal md:min-h-0 md:whitespace-nowrap'
        onClick={onNext}
        disabled={!otpauthUrl}
      >
        {t('mfaSetup.next')}
      </Button>
      <div className='flex justify-center'>
        <MfaLinkButton muted onClick={onBack}>
          {t('mfaVerify.back')}
        </MfaLinkButton>
      </div>
    </div>
  )
}

/* ── MfaSetupConfirmStep ────────────────────────────────────────────────── */

export type MfaSetupConfirmStepProps = {
  totpCode: string
  setTotpCode: (code: string) => void
  isLoading: boolean
  onSubmit: (event: FormEvent) => void
  onBack: () => void
  error?: string
}

export const MfaSetupConfirmStep = ({
  totpCode,
  setTotpCode,
  isLoading,
  onSubmit,
  onBack,
  error,
}: MfaSetupConfirmStepProps) => {
  const { t } = useTranslation()

  return (
    <form className='space-y-3.5' onSubmit={onSubmit} noValidate>
      <MfaStepHeader
        icon={<ShieldCheck className='size-5' aria-hidden />}
        title={t('mfaSetup.confirmTitle')}
        subtitle={t('mfaSetup.confirmInstruction')}
      />
      <MfaCodeInput value={totpCode} onChange={setTotpCode} />
      {error ? <InlineError>{error}</InlineError> : null}
      <Button
        type='submit'
        className='min-h-11 w-full whitespace-normal md:min-h-0 md:whitespace-nowrap'
        disabled={totpCode.length !== 6}
        loading={isLoading}
        loadingLabel={t('mfaVerify.verifying')}
      >
        {t('mfaSetup.enable')}
      </Button>
      <div className='flex justify-center'>
        <MfaLinkButton muted onClick={onBack}>
          {t('mfaVerify.back')}
        </MfaLinkButton>
      </div>
    </form>
  )
}

/* ── MfaSetupEmailOtpStep ───────────────────────────────────────────────── */

export type MfaSetupEmailOtpStepProps = {
  emailOtpCode: string
  setEmailOtpCode: (code: string) => void
  isLoading: boolean
  emailSending: boolean
  emailSent: boolean
  emailError: string
  resendSecondsLeft: number
  onSubmit: (event: FormEvent) => void
  onResend: () => void
  onBack: () => void
}

export const MfaSetupEmailOtpStep = ({
  emailOtpCode,
  setEmailOtpCode,
  isLoading,
  emailSending,
  emailSent,
  emailError,
  resendSecondsLeft,
  onSubmit,
  onResend,
  onBack,
}: MfaSetupEmailOtpStepProps) => {
  const { t } = useTranslation()

  return (
    <form className='space-y-3.5' onSubmit={onSubmit} noValidate>
      <MfaStepHeader
        icon={<Mail className='size-5' aria-hidden />}
        title={t('mfaSetup.emailOtpTitle')}
        subtitle={emailSent ? t('mfaSetup.emailOtpInstruction') : t('mfaVerify.sendingEmail')}
      />
      <MfaCodeInput value={emailOtpCode} onChange={setEmailOtpCode} />
      {emailError ? <InlineError>{emailError}</InlineError> : null}
      {/* `!emailSent` blokuje wysyłkę kodu, zanim backend potwierdzi wysłanie e-maila. */}
      <Button
        type='submit'
        className='min-h-11 w-full whitespace-normal md:min-h-0 md:whitespace-nowrap'
        disabled={emailOtpCode.length !== 6 || !emailSent}
        loading={isLoading}
        loadingLabel={t('mfaVerify.verifying')}
      >
        {t('mfaSetup.enable')}
      </Button>
      <div className='flex flex-col items-center gap-2'>
        <MfaLinkButton disabled={resendSecondsLeft > 0 || emailSending} onClick={onResend}>
          {resendSecondsLeft > 0
            ? t('mfaVerify.resendCooldown', { seconds: resendSecondsLeft })
            : t('mfaVerify.resend')}
        </MfaLinkButton>
        <MfaLinkButton muted onClick={onBack}>
          {t('mfaVerify.back')}
        </MfaLinkButton>
      </div>
    </form>
  )
}

/* ── MfaSetupBackupCodesStep ────────────────────────────────────────────── */

export type MfaSetupBackupCodesStepProps = {
  backupCodes: string[]
  continueLabel: string
  warning?: string
  onContinue: () => void
  onDownload?: () => void
}

export const MfaSetupBackupCodesStep = ({
  backupCodes,
  continueLabel,
  warning,
  onContinue,
  onDownload,
}: MfaSetupBackupCodesStepProps) => {
  const { t } = useTranslation()
  const [codesCopied, setCodesCopied] = React.useState(false)
  const [codesDownloaded, setCodesDownloaded] = React.useState(false)

  /* Potwierdzenie skopiowania/pobrania jest zachowaniem produkcyjnym — kody
     pokazujemy raz, więc użytkownik musi wiedzieć, że akcja się powiodła. */
  const copyAll = () => {
    navigator.clipboard.writeText(backupCodes.join('\n')).then(() => {
      setCodesCopied(true)
      setTimeout(() => setCodesCopied(false), 2500)
    })
  }

  const download = () => {
    onDownload?.()
    setCodesDownloaded(true)
    setTimeout(() => setCodesDownloaded(false), 2500)
  }

  return (
    <div className='space-y-3.5'>
      <MfaStepHeader
        icon={<ShieldCheck className='size-5' aria-hidden />}
        title={t('mfaSetup.backupTitle')}
        subtitle={warning ?? t('mfaSetup.backupWarning')}
      />

      <div className='grid gap-2 grid-cols-1 md:grid-cols-2'>
        {backupCodes.map((code) => (
          <code
            key={code}
            className='rounded-crane-md bg-crane-card-inset px-2.5 py-2 text-center text-crane-lg font-semibold tabular-nums tracking-wider text-crane-foreground'
          >
            {code}
          </code>
        ))}
      </div>

      <div className='flex flex-col gap-2 md:flex-row md:justify-center'>
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='min-h-11 whitespace-normal md:min-h-0 md:whitespace-nowrap'
          onClick={copyAll}
        >
          {codesCopied ? <CheckCircle2 className='text-crane-status-online' aria-hidden /> : <Copy aria-hidden />}
          {codesCopied ? t('mfaSetup.codesCopied') : t('mfaSetup.copyAll')}
        </Button>
        {onDownload ? (
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='min-h-11 whitespace-normal md:min-h-0 md:whitespace-nowrap'
            onClick={download}
          >
            {codesDownloaded ? (
              <CheckCircle2 className='text-crane-status-online' aria-hidden />
            ) : (
              <Download aria-hidden />
            )}
            {codesDownloaded ? t('mfaSetup.codesDownloaded') : t('mfaSetup.download')}
          </Button>
        ) : null}
      </div>

      <Button
        type='button'
        className='min-h-11 w-full whitespace-normal md:min-h-0 md:whitespace-nowrap'
        onClick={onContinue}
      >
        {continueLabel}
      </Button>
    </div>
  )
}
